import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import {
	readAppMode,
	writeAppMode,
	type AppMode,
} from "./admin.js";

type AppModeContextValue = {
	mode: AppMode;
	enterAdmin: () => void;
	enterMatrix: () => void;
};

const AppModeContext = createContext<AppModeContextValue | null>(null);

export function AppModeProvider({ children }: { children: ReactNode }) {
	const [mode, setMode] = useState<AppMode>(() => readAppMode());

	useEffect(() => {
		const onHash = () => {
			const hash = window.location.hash.replace(/^#/, "").toLowerCase();
			if (hash === "admin") {
				setMode("admin");
				writeAppMode("admin");
				return;
			}
			if (readAppMode() === "admin" && hash !== "admin") {
				/* keep stored mode unless user cleared hash intentionally */
			}
		};
		window.addEventListener("hashchange", onHash);
		return () => window.removeEventListener("hashchange", onHash);
	}, []);

	const enterAdmin = useCallback(() => {
		writeAppMode("admin");
		setMode("admin");
	}, []);

	const enterMatrix = useCallback(() => {
		writeAppMode("matrix");
		setMode("matrix");
	}, []);

	const value = useMemo(
		() => ({ mode, enterAdmin, enterMatrix }),
		[mode, enterAdmin, enterMatrix],
	);

	return (
		<AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>
	);
}

export function useAppMode(): AppModeContextValue {
	const ctx = useContext(AppModeContext);
	if (!ctx) {
		throw new Error("useAppMode must be used within AppModeProvider");
	}
	return ctx;
}
